import React, { useState, useEffect } from 'react';
import { baseUrl, envVar } from '../../../config/baseUrl';
import { errerMessages } from '../../../helpers/error-messages-enum';
import { deviceInfo, threeDsRedirect } from '../../../helpers';
import GooglePayButton from '@google-pay/button-react';

// via = btoa('frontend-crst-gpay')
const VIA_PARAM = 'ZnJvbnRlbmQtY3JzdC1ncGF5';

export default function GooglePayCS({ encryptData, base64Data, data, setError, setMessage, setFailedMessage, setLoading }) {
  const defaultNetworks = envVar.gPaySupportedCardNetworks?.split(',') || ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];
  const [allowedCardNetworks, setAllowedCardNetworks] = useState(defaultNetworks);

  const totalPrice = parseFloat(data?.total)?.toFixed(2);

  useEffect(() => {
    let cardList = envVar.gPaySupportedCardNetworks?.split(',') || ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];
    if (data?.exclude_card_networks) {
      const excludeList = data.exclude_card_networks.toUpperCase().split(',');
      cardList = cardList.filter(card => !excludeList.includes(card));
    }
    setAllowedCardNetworks(cardList);
  }, [data]);

  const initiatePayment = async (gpayData) => {
    try {
      setLoading(true);

      const rawResponse = await fetch(
        `${baseUrl.earth}/sale/digital-wallet?via=${VIA_PARAM}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(gpayData),
        }
      );

      // success
      if (rawResponse.status === 200) {
        const responseData = await rawResponse.json();
        const redirectUrl = responseData?.data?.redirectUrl || data?.redirect_url;
        redirectUrl && window.location.replace(redirectUrl);
        return;
      }

      // 3DS required
      if (rawResponse.status === 201) {
        const responseData = await rawResponse.json();
        const { success, threeDSreq, order_id } = responseData.data;
        if (success === '3dsV2' && threeDSreq?.hasOwnProperty('threeDSMethodData')) {
          await threeDsRedirect.threeDsV2AcsRedirect(threeDSreq, { orderId: order_id });
        } else if (success === '3dsV2' && threeDSreq?.hasOwnProperty('creq')) {
          threeDsRedirect.AcsRedirect({
            action: 'creq',
            threeDSURL: threeDSreq.threeDSURL,
            creq: threeDSreq.creq,
          });
        } else {
          setLoading(false);
          setError(true);
          setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #GPCS710`);
          setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
        }
        return;
      }

      // Error responses
      setLoading(false);
      setError(true);
      if (rawResponse.status === 422) {
        const errorData = await rawResponse.json();
        setFailedMessage(errorData?.message || errerMessages.SOMETHING_WENT_WRONG);
      } else if (rawResponse.status === 402) {
        setMessage(`${errerMessages.KOUNT_FAILED_ERROR} #GPCS708KT`);
        setFailedMessage(errerMessages.PAYMENT_DECLINED_FOR_SECURITY_REASONS);
      } else {
        setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #GPCS705`);
        setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
      }
    } catch (error) {
      console.error('GooglePayCS error', error);
      setLoading(false);
      setError(true);
      setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #GPCS706`);
      setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
    }
  };

  const onLoadPaymentData = (paymentRequest) => {
    const paymentMethodData = paymentRequest?.paymentMethodData;
    const token = paymentMethodData?.tokenizationData?.token;
    const billingAddress = paymentMethodData?.info?.billingAddress;

    initiatePayment({
      data: encryptData,
      token: JSON.parse(token),
      base64Data,
      billingAddress,
      kountSessionId: localStorage.kSessionId,
      origin: 'datman_v1',
      deviceInfo: deviceInfo.GetDeviceInfo(),
    });
  };

  return (
    <GooglePayButton
      environment={envVar.gPayEnv}
      buttonType='pay'
      buttonSizeMode='fill'
      paymentRequest={{
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks,
              billingAddressRequired: true,
              billingAddressParameters: {
                format: 'FULL',
                phoneNumberRequired: false,
              },
            },
            tokenizationSpecification: {
              type: 'PAYMENT_GATEWAY',
              parameters: {
                gateway: 'crst',
                gatewayMerchantId: String(data?.cs_merchant_id),
              },
            },
          },
        ],
        merchantInfo: {
          merchantId: envVar.gPayMerchantId,
          merchantName: envVar.gPayMerchantName,
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPriceLabel: 'Total',
          totalPrice,
          currencyCode: data?.currency_name,
          countryCode: data?.country_code,
        },
      }}
      onLoadPaymentData={onLoadPaymentData}
      onError={error => console.error('GooglePayCS onError', error)}
    />
  );
}
